import type {
  MiroFishAgentAction,
  MiroFishAgentProfile,
  MiroFishChannelRole,
  MiroFishSimulationAnalytics,
  MiroFishSimulationPayload,
} from "@/types/mirofish";

const MEANINGFUL_ACTION_EXCLUSIONS = new Set([
  "DO_NOTHING",
  "SEARCH_POSTS",
  "SEARCH_USER",
]);

const RESISTANCE_ACTIONS = new Set(["DISLIKE_POST", "DISLIKE_COMMENT", "MUTE"]);

const AMPLIFICATION_ACTIONS = new Set([
  "LIKE_POST",
  "LIKE_COMMENT",
  "REPOST",
  "QUOTE_POST",
  "FOLLOW",
]);

const round4 = (value: number) => Math.round(value * 10_000) / 10_000;

const ratio = (numerator: number, denominator: number) =>
  denominator > 0 ? round4(numerator / denominator) : null;

const clampScore = (value: number) => Math.max(-1, Math.min(1, value));

const normalizeActionType = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const profileActorId = (profile: MiroFishAgentProfile) =>
  String(profile.actorId || profile.id).replace(/^profile_/, "");

const actionActorId = (action: MiroFishAgentAction) =>
  action.actorId ? String(action.actorId).replace(/^profile_/, "") : "";

const dispositionFor = (profile: MiroFishAgentProfile) => {
  const stance = profile.stance.trim().toLowerCase();
  if (stance === "supportive" || stance.includes("supportive")) {
    return "positive" as const;
  }
  if (stance === "opposing" || stance.includes("opposing")) {
    return "detractor" as const;
  }

  const bias = Number(profile.sentimentBias);
  if (Number.isFinite(bias) && bias > 0.1) return "positive" as const;
  if (Number.isFinite(bias) && bias < -0.1) return "detractor" as const;
  return "neutral" as const;
};

export const inferMiroFishChannelRole = (
  profile: Pick<MiroFishAgentProfile, "channelRole" | "role">,
): MiroFishChannelRole => {
  if (profile.channelRole && profile.channelRole !== "none") {
    return profile.channelRole;
  }

  const role = profile.role.toLowerCase();
  if (
    /journal|news|media|press|publisher|broadcaster|报社|媒体|记者/.test(role)
  ) {
    return "media";
  }
  if (/platform|network|marketplace|平台/.test(role)) return "platform";
  if (/distribut|retail|wholesale|channel|reseller|经销|渠道|零售/.test(role)) {
    return "distributor";
  }
  if (/influencer|creator|blogger|celebrity|意见领袖|博主|网红/.test(role)) {
    return "influencer";
  }
  if (
    /regulator|government|association|institution|gatekeeper|监管|政府|协会|机构/.test(
      role,
    )
  ) {
    return "institutional_gatekeeper";
  }
  return "none";
};

export function buildMiroFishSimulationAnalytics({
  graphNodeCount,
  simulation,
  actions = simulation.actions,
}: {
  graphNodeCount: number;
  simulation: Pick<
    MiroFishSimulationPayload,
    | "profiles"
    | "actions"
    | "currentRound"
    | "totalRounds"
    | "config"
    | "status"
  >;
  actions?: MiroFishAgentAction[];
}): MiroFishSimulationAnalytics {
  const profiles = simulation.profiles;
  const profilesById = new Map<string, MiroFishAgentProfile>();
  const profilesByName = new Map<string, MiroFishAgentProfile>();
  for (const profile of profiles) {
    profilesById.set(profileActorId(profile), profile);
    profilesByName.set(profile.name.trim().toLowerCase(), profile);
  }

  const resolveActor = (action: MiroFishAgentAction) =>
    profilesById.get(actionActorId(action)) ||
    profilesByName.get(action.actor.trim().toLowerCase());

  const meaningfulActions = actions.filter(
    (action) =>
      action.success !== false &&
      !MEANINGFUL_ACTION_EXCLUSIONS.has(normalizeActionType(action.actionType)),
  );
  const actorActions = new Map<string, MiroFishAgentAction[]>();
  for (const action of meaningfulActions) {
    const profile = resolveActor(action);
    if (!profile) continue;
    const id = profileActorId(profile);
    actorActions.set(id, [...(actorActions.get(id) || []), action]);
  }

  let positiveActorCount = 0;
  let detractorActorCount = 0;
  let neutralActorCount = 0;
  let weightedSentiment = 0;
  let populationWeight = 0;
  let activeSentiment = 0;
  let activeWeight = 0;

  for (const profile of profiles) {
    const disposition = dispositionFor(profile);
    if (disposition === "positive") positiveActorCount += 1;
    else if (disposition === "detractor") detractorActorCount += 1;
    else neutralActorCount += 1;

    const bias = clampScore(
      Number.isFinite(Number(profile.sentimentBias))
        ? Number(profile.sentimentBias)
        : disposition === "positive"
          ? 1
          : disposition === "detractor"
            ? -1
            : 0,
    );
    const influence =
      Number.isFinite(Number(profile.influenceWeight)) &&
      Number(profile.influenceWeight) > 0
        ? Number(profile.influenceWeight)
        : 1;
    weightedSentiment += bias * influence;
    populationWeight += influence;

    const actionCount = actorActions.get(profileActorId(profile))?.length || 0;
    if (actionCount) {
      activeSentiment += bias * influence * actionCount;
      activeWeight += influence * actionCount;
    }
  }

  const populationScore =
    populationWeight > 0 ? round4(weightedSentiment / populationWeight) : null;
  const activeConversationScore =
    activeWeight > 0 ? round4(activeSentiment / activeWeight) : null;
  const simulatedActorCount = profiles.length;
  const reachedActorCount = actorActions.size;

  const firstRoundByActor = new Map<string, number>();
  for (const [actorId, actorActionList] of actorActions) {
    firstRoundByActor.set(
      actorId,
      Math.min(...actorActionList.map((action) => Math.max(0, action.round))),
    );
  }
  const newActorsByRound = new Map<number, number>();
  for (const round of firstRoundByActor.values()) {
    newActorsByRound.set(round, (newActorsByRound.get(round) || 0) + 1);
  }
  const sortedRounds = [...newActorsByRound.keys()].sort(
    (left, right) => left - right,
  );
  const timeToThreshold = (fraction: number) => {
    if (!simulatedActorCount) return null;
    const threshold = Math.ceil(simulatedActorCount * fraction);
    let cumulative = 0;
    for (const round of sortedRounds) {
      cumulative += newActorsByRound.get(round) || 0;
      if (cumulative >= threshold) {
        return round * Math.max(1, simulation.config.minutesPerRound);
      }
    }
    return null;
  };
  const peakRound = sortedRounds.reduce<number | null>((current, round) => {
    if (current === null) return round;
    return (newActorsByRound.get(round) || 0) >
      (newActorsByRound.get(current) || 0)
      ? round
      : current;
  }, null);

  const completedRounds =
    simulation.status === "completed"
      ? Math.max(simulation.currentRound, simulation.totalRounds)
      : simulation.currentRound;
  const simulatedMinutes =
    Math.max(0, completedRounds) *
    Math.max(1, simulation.config.minutesPerRound);
  const simulatedHours = simulatedMinutes / 60;

  const channelProfiles = profiles.filter(
    (profile) => inferMiroFishChannelRole(profile) !== "none",
  );
  const channelIds = new Set(channelProfiles.map(profileActorId));
  const resistantActorCount = channelProfiles.filter(
    (profile) => dispositionFor(profile) === "detractor",
  ).length;
  let resistanceActionCount = 0;
  let amplificationActionCount = 0;
  for (const action of meaningfulActions) {
    const profile = resolveActor(action);
    if (!profile || !channelIds.has(profileActorId(profile))) continue;
    const type = normalizeActionType(action.actionType);
    if (RESISTANCE_ACTIONS.has(type)) resistanceActionCount += 1;
    if (AMPLIFICATION_ACTIONS.has(type)) amplificationActionCount += 1;
  }

  return {
    methodVersion: "simulation-aggregates.v1",
    population: {
      graphNodeCount: Math.max(0, Math.floor(graphNodeCount || 0)),
      simulatedActorCount,
      activeActorCount: reachedActorCount,
      positiveActorCount,
      detractorActorCount,
      neutralActorCount,
      positiveRatio: ratio(positiveActorCount, simulatedActorCount),
      detractorRatio: ratio(detractorActorCount, simulatedActorCount),
      neutralRatio: ratio(neutralActorCount, simulatedActorCount),
      netDispositionScore: ratio(
        positiveActorCount - detractorActorCount,
        simulatedActorCount,
      ),
    },
    sentiment: {
      score: activeConversationScore ?? populationScore,
      populationScore,
      activeConversationScore,
      basis:
        activeConversationScore !== null
          ? "activity_weighted"
          : populationScore !== null
            ? "population_weighted"
            : "unavailable",
    },
    propagation: {
      reachedActorCount,
      reachRatio: ratio(reachedActorCount, simulatedActorCount),
      simulatedMinutes,
      actorsPerSimulatedHour:
        simulatedHours > 0 ? round4(reachedActorCount / simulatedHours) : 0,
      actionsPerSimulatedHour:
        simulatedHours > 0
          ? round4(meaningfulActions.length / simulatedHours)
          : 0,
      timeTo25PercentMinutes: timeToThreshold(0.25),
      timeTo50PercentMinutes: timeToThreshold(0.5),
      peakRound,
    },
    channelResistance: {
      channelActorCount: channelProfiles.length,
      resistantActorCount,
      oppositionRatio: ratio(resistantActorCount, channelProfiles.length),
      resistanceActionCount,
      amplificationActionCount,
      behavioralResistanceRatio: ratio(
        resistanceActionCount,
        resistanceActionCount + amplificationActionCount,
      ),
    },
  };
}
